/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useMemo, useState } from 'react';

import { OverflowTooltip } from '@class-ui/react-components';
import {
    ArrowBackIosNew as ArrowBackIcon,
    ArrowForwardIos as ArrowForwardIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Menu as MenuIcon,
    MoreHoriz as MoreHorizIcon,
    Settings as SettingsIcon,
    KeyboardCommandKey as ShortcutsIcon,
    DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import DrawIcon from '@mui/icons-material/Draw';
import NotesIcon from '@mui/icons-material/Notes';
import {
    AppBar as MuiAppBar,
    AppBarProps as MuiAppBarProps,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    Toolbar,
    Typography
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styledComponent from 'styled-components';

import ClearAllConfirmModal from './components/interface/delete/ClearAllConfirmModal';
import ElementTools from './components/interface/ElementTools';
import HelpModal from './components/interface/help/HelpModal';
import { WhiteboardContext } from './context/WhiteboardContext';
import { WhiteboardSessionContext } from './context/WhiteboardSessionContext';
import { useWhiteboardSubtitle, useWhiteboardTitle } from './hooks';
import PresentingControls from './PresentingControls';
import { PageActions } from './state/WhiteboardSessionState';
import { SessionTypes } from './types';
import { checkPagePermission } from './util/misc';
import WhiteboardSidebarContent from './WhiteboardSidebarContent';
import { checkPermission } from '../../utils/permission';
import { getUrlParam } from '../../utils/url';
import { usePrevious } from '../../utils/usePrevious';
import { ActionMenu, ActionMenuItem } from '../common/table/ActionMenu';
import { UserRoles } from '../roster/types';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    marginLeft: `-${drawerWidth}px`,
    height: '100vh',
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0
    })
}));

const DocumentNavigation = styled(Box)``;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
    background: '#fff',
    color: '#000',
    zIndex: 2,
    '& .MuiToolbar-root': {
        minHeight: '48px'
    },
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
    height: '48px',
    minHeight: '48px !important'
}));

const StyledDrawer = styledComponent(Drawer)`
    background-color: ${(props) => props.theme.colors.backgroundLight};
    
    & .MuiPaper-root {
        background: transparent;
        box-sizing: 'border-box'
    }
`;

const Subtitle = styled(Typography)`
    word-break: break-all;
`;

const WhiteboardSidebarContentHolder = styled(Box)`
    overflow-y: auto;
`;

const ZoomToolsHolder = styled(Box)`
    margin-left: auto;
`;

const DrawerTitle = styled(Typography)`
    word-wrap: break-word;
    max-width: 100%;
    max-height: 44px;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const WhiteboardLayout: React.FC<{
    children: React.ReactNode;
    onNextPage?: () => void;
    onPrevPage?: () => void;
}> = (props) => {
    const { t } = useTranslation('whiteboard');
    const { userInfo, updatePages } = useContext(WhiteboardContext);
    const {
        setShowSettingsDialog,
        sessionOptions,
        whiteboardSessionState,
        clearPage,
        dispatchWhiteboardSessionAction,
        setShowNotes,
        showNotes,
        elementOptionsPanel,
        zoomTools
    } = useContext(WhiteboardSessionContext);
    const { session, currentPage, presenter, hasNotes } = whiteboardSessionState;

    const isReadOnly = getUrlParam('readOnly') === 'true';

    const theme = useTheme();
    const title = useWhiteboardTitle();
    const subtitle = useWhiteboardSubtitle();

    const { onNextPage, onPrevPage } = props;
    const previousElementOptionsPanel = usePrevious(elementOptionsPanel);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const [clearAllModalOpen, setClearAllModalOpen] = useState(false);
    const isPresenter = useMemo(
        () => presenter && presenter.student_id && presenter.student_id === userInfo?.student_id,
        [presenter, userInfo?.student_id]
    );
    const userRole = userInfo?.role || UserRoles.Student;
    const hidePages = useMemo(
        () =>
            !isReadOnly &&
            ((presenter && !isPresenter) ||
                (session?.type === SessionTypes.Individual && userRole === UserRoles.Student)),
        [presenter, isPresenter, session?.type, userRole, isReadOnly]
    );

    useMemo(() => {
        if (!isReadOnly && elementOptionsPanel !== previousElementOptionsPanel) {
            setDrawerOpen(true);
        }
    }, [elementOptionsPanel, previousElementOptionsPanel, isReadOnly]);

    const showDrawer = !isReadOnly && drawerOpen && (!hidePages || !!elementOptionsPanel);

    const showWhiteboardActions = !isReadOnly && checkPermission(UserRoles.Assistant, userRole);

    const showNavigation = useMemo(() => {
        return !isReadOnly;
        if (session?.type === SessionTypes.Individual) {
            return false;
        }

        return (!!onNextPage || !!onPrevPage) && sessionOptions.allowPageNavigation;
    }, [onNextPage, onPrevPage, session?.type, sessionOptions.allowPageNavigation, isReadOnly]);

    const canClearAnnotations =
        (whiteboardSessionState.session?.type !== SessionTypes.Shared &&
            whiteboardSessionState.session?.type !== SessionTypes.Presentation) ||
        userRole !== UserRoles.Student;

    const canEdit = useMemo(() => {
        if (isReadOnly) {
            return false;
        }
        // If page is lock no one can edit including instructors.
        if (!currentPage || currentPage.is_locked) {
            return false;
        }

        if (userInfo && currentPage && session?.type) {
            return (
                checkPagePermission(currentPage, userInfo, session.type) &&
                (session.type === SessionTypes.Presentation ||
                    !presenter ||
                    presenter.student_id === userInfo.student_id ||
                    session.type === SessionTypes.Individual)
            );
        }
        return false;
    }, [presenter, userInfo, currentPage, session, isReadOnly]);

    const actions: ActionMenuItem[] = [
        Boolean(sessionOptions.displayEditingToggle) &&
            !sessionOptions.studentEditingEnabled && {
                name: t('allow-student-editing'),
                keepOpen: true,
                onClick: () => {
                    if (currentPage) {
                        const newValue = !sessionOptions.studentEditingEnabled;

                        updatePages([
                            {
                                ...currentPage,
                                write_access: newValue ? UserRoles.Student : UserRoles.Assistant
                            }
                        ]);
                        dispatchWhiteboardSessionAction({
                            type: PageActions.Updated,
                            payload: {
                                pages: [
                                    {
                                        ...currentPage,
                                        write_access: newValue ? UserRoles.Student : UserRoles.Assistant
                                    }
                                ]
                            }
                        });
                    }
                },
                icon: <DrawIcon />
            },
        Boolean(sessionOptions.displayEditingToggle) &&
            sessionOptions.studentEditingEnabled && {
                name: t('disallow-student-editing'),
                keepOpen: true,
                onClick: () => {
                    if (currentPage) {
                        const newValue = !sessionOptions.studentEditingEnabled;

                        updatePages([
                            {
                                ...currentPage,
                                write_access: newValue ? UserRoles.Student : UserRoles.Assistant
                            }
                        ]);
                        dispatchWhiteboardSessionAction({
                            type: PageActions.Updated,
                            payload: {
                                pages: [
                                    {
                                        ...currentPage,
                                        write_access: newValue ? UserRoles.Student : UserRoles.Assistant
                                    }
                                ]
                            }
                        });
                    }
                },
                icon: <DrawIcon />
            },
        Boolean(hasNotes && isPresenter && showNotes) && {
            name: t('stop-displaying-notes'),
            onClick: () => setShowNotes(false),
            icon: <NotesIcon />
        },
        Boolean(hasNotes && isPresenter && !showNotes) && {
            name: t('start-displaying-notes'),
            onClick: () => setShowNotes(true),
            icon: <NotesIcon />
        },
        {
            name: t('shortcuts'),
            onClick: () => setHelpModalOpen(true),
            icon: <ShortcutsIcon />
        },
        {
            name: t('common:settings'),
            onClick: () => setShowSettingsDialog(true),
            icon: <SettingsIcon />
        },
        canClearAnnotations && {
            name: t('clearAll'),
            onClick: () => setClearAllModalOpen(true),
            icon: <DeleteIcon color="error" />,
            menuItemProps: {
                sx: {
                    color: 'error.main'
                }
            }
        }
    ].filter(Boolean) as ActionMenuItem[];

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {!isReadOnly && (
                <StyledDrawer
                    sx={{
                        width: drawerWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiDrawer-paper': {
                            width: drawerWidth
                        }
                    }}
                    variant="persistent"
                    anchor="left"
                    open={showDrawer || (drawerOpen && (!hidePages || !!elementOptionsPanel))}
                >
                    <DrawerHeader>
                        <DrawerTitle variant="body1" justifyContent="left">
                            {title}
                        </DrawerTitle>

                        <IconButton onClick={() => setDrawerOpen(false)}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>

                    <Divider />

                    {canEdit && (
                        <>
                            {elementOptionsPanel}
                            {elementOptionsPanel && <Divider />}
                        </>
                    )}

                    {!hidePages && (
                        <WhiteboardSidebarContentHolder>
                            <WhiteboardSidebarContent />
                        </WhiteboardSidebarContentHolder>
                    )}
                </StyledDrawer>
            )}

            <Main open={showDrawer}>
                <AppBar position="relative" open={showDrawer} elevation={1}>
                    {!isReadOnly && (
                        <Toolbar>
                            {(!!elementOptionsPanel || !hidePages) && (
                                <IconButton
                                    color="inherit"
                                    aria-label={t('openDrawer')}
                                    onClick={() => setDrawerOpen(true)}
                                    edge="start"
                                    sx={{ mr: 2, ...(showDrawer && { display: 'none' }) }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}

                            <DocumentNavigation>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showNavigation && !hidePages && (
                                        <IconButton
                                            color="inherit"
                                            aria-label={t('navigation.previousPage')}
                                            onClick={onPrevPage}
                                            disabled={!onPrevPage}
                                        >
                                            <ArrowBackIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {subtitle && (
                                        <Subtitle
                                            sx={{ ml: 1, mr: 1, maxWidth: '120px', position: 'relative', top: '-2px' }}
                                        >
                                            <OverflowTooltip>{subtitle}</OverflowTooltip>
                                        </Subtitle>
                                    )}
                                    {showNavigation && !hidePages && (
                                        <IconButton
                                            color="inherit"
                                            aria-label={t('navigation.nextPage')}
                                            onClick={onNextPage}
                                            disabled={!onNextPage}
                                        >
                                            <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </DocumentNavigation>

                            {zoomTools && <ZoomToolsHolder>{zoomTools}</ZoomToolsHolder>}
                            <ElementTools canEdit={canEdit} />
                            <PresentingControls />

                            {showWhiteboardActions && (
                                <ActionMenu
                                    uniqueId="actions"
                                    title={t('sessionActions')}
                                    actions={actions}
                                    component="icon-button"
                                >
                                    <MoreHorizIcon />
                                </ActionMenu>
                            )}
                        </Toolbar>
                    )}
                </AppBar>

                <ClearAllConfirmModal
                    open={clearAllModalOpen}
                    onClose={() => setClearAllModalOpen(false)}
                    onConfirm={() => {
                        clearPage();
                        setClearAllModalOpen(false);
                    }}
                />
                <HelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />

                {props.children}
            </Main>
        </Box>
    );
};
